import { AxiosError } from 'axios';
import { storeToRefs } from 'pinia';
import { catchError, map, of, tap, throwError } from 'rxjs';
import { useRouter } from 'vue-router';

import { ROUTE_KEYS } from '~/router';
import { useBackendHttpService } from '~/services/http';
import { useAuthStore } from '~/stores';

const backendURL = import.meta.env.CE_BACKEND_BASE_URL;

export function initBackend(): void {
	const { setBackendURL, setOnError } = useBackendHttpService();
	setBackendURL(backendURL);
	const authStore = useAuthStore();
	const { authSession } = storeToRefs(authStore);
	const { fetchAuthSession, validateTokenExpiration, signOut } = authStore;
	const router = useRouter();
	const onError = (err: AxiosError) => {
		//TODO: switch case with common error status
		// We should probably plug it to a notification service
		// allowing us to show these errors as toasts with a relevant translated message.
		if (err?.response?.status === 401) {
			const authSession$ =
				authSession.value && validateTokenExpiration(authSession.value) ? of(authSession.value) : fetchAuthSession();
			return authSession$.pipe(
				map(session => {
					/**
					 * When failing to refresh auth session, no error is thrown, but we get an obj with attrs undefined:
					 * { tokens: undefined, credentials: undefined, identityId: undefined, userSub: undefined }
					 * hence we manually throw an error when needed and redirect to sign in page
					 */
					if (validateTokenExpiration(session)) {
						return session;
					} else {
						throw new Error('failed to refresh token');
					}
				}),
				catchError(() => {
					if (!!authSession.value) {
						signOut().pipe(tap(() => router.push({ name: ROUTE_KEYS.AUTH_SIGN_IN })));
					} else {
						return router.push({ name: ROUTE_KEYS.AUTH_SIGN_IN });
					}
				})
			);
		}
		return throwError(() => {
			return err;
		});
	};
	setOnError(onError);
}
